import React from 'react';

import { Icon, Text } from '@betterment/samba-react';
import { Box, Flex, ToggleSwitch } from '@betterment/style-closet-react';

import { trackClick } from '../../features/track';
import { CHECKING_SUPPORT_PHONE } from '../../Shared/constants';
import { Account, Card } from '../data';
import { isLocked, isUnlocked, isUserUnlockable } from '../models/card';

export interface Props {
  card: Card;
  account: Account;
  onLockCard: () => Promise<void>;
  onUnlockCard: () => Promise<void>;
}

const CardLockUnlock: React.FC<React.PropsWithChildren<Props>> = ({ onLockCard, onUnlockCard, card, account }) => {
  const [isLoading, setLoading] = React.useState(false);
  const isCardLocked: boolean = isLocked(card);
  const isCardUnlockable: boolean = isCardLocked && isUserUnlockable(card);
  const isCardLockable: boolean = isUnlocked(card);

  const onToggleClick = async () => {
    setLoading(true);

    try {
      if (isCardUnlockable) {
        await onUnlockCard();
      } else if (isCardLockable) {
        await onLockCard();
      }
    } finally {
      setLoading(false);
    }
  };

  const shouldDisplayToggleLockSwitch = isCardUnlockable || isCardLockable;
  const accountType = account.isJointAccount ? 'Joint' : 'Individual';
  const toggleSwitchName = isCardUnlockable ? 'UnlockCard' : 'LockCard';

  return (
    <Box pb="4">
      <Box>
        <Flex justifyContent="space-between" alignItems="flex-start">
          <Flex alignItems="flex-start">
            <Box mr="3">
              <Icon name="Lock" size="small" />
            </Box>
            <Text
              as="p"
              css={{
                margin: 'space.0',
                paddingBottom: 'space.50',
                textAlign: 'left',
              }}
            >
              Card lock
            </Text>
          </Flex>
          {shouldDisplayToggleLockSwitch && (
            <ToggleSwitch
              role="switch"
              name="cardLockToggle"
              color="bmtOrange90"
              size="small"
              defaultChecked={isCardLocked}
              onClick={() => {
                trackClick({ name: `${toggleSwitchName}-${accountType}` });
                void onToggleClick();
              }}
            />
          )}
        </Flex>
        {!isLoading && isCardLocked && (
          <Flex justifyContent="space-between" alignItems="flex-start">
            <Text
              as="p"
              color="text.weak"
              css={{
                fontSize: 'fontSize.50',
                marginTop: 'space.100',
                marginLeft: 'space.400',
              }}
            >
              <Text
                color="text.weak"
                css={{
                  fontWeight: 'bold',
                }}
              >
                {' '}
                Your card is currently locked
              </Text>{' '}
              and can't be used.
              {!isCardUnlockable && (
                <>
                  {' '}
                  Please contact us at <a href={`tel:${CHECKING_SUPPORT_PHONE}`}>
                    {CHECKING_SUPPORT_PHONE}
                  </a> during{' '}
                  <a href="https://www.betterment.com/contact/" target="_blank" rel="noopener noreferrer">
                    business hours
                  </a>{' '}
                  to unlock your card.
                </>
              )}
              {isCardUnlockable && (
                <>
                  {' '}
                  Please unlock your card to continue using it or contact us at{' '}
                  <a href={`tel:${CHECKING_SUPPORT_PHONE}`}>{CHECKING_SUPPORT_PHONE}</a> during{' '}
                  <a href="https://www.betterment.com/contact/" target="_blank" rel="noopener noreferrer">
                    business hours
                  </a>{' '}
                  if your card was lost, stolen, or damaged.
                </>
              )}
            </Text>
          </Flex>
        )}
      </Box>
      {isLoading && isCardUnlockable && (
        <Text
          color="text.weak"
          css={{
            paddingBottom: 'space.50',
            fontSize: 'fontSize.50',
            marginLeft: 'space.400',
          }}
        >
          Unlocking your card...
        </Text>
      )}
      {isLoading && isCardLockable && (
        <Text
          color="text.weak"
          css={{
            paddingBottom: 'space.50',
            fontSize: 'fontSize.50',
            marginLeft: 'space.400',
          }}
        >
          Locking your card...
        </Text>
      )}
    </Box>
  );
};

export default CardLockUnlock;
